/**
 * Onboarding Foundation — IN-2.a
 * ──────────────────────────────
 * Pure type definitions for the installer onboarding provider.
 *
 * Hard rules (IN-2.a):
 *   • No wizard UI yet.
 *   • No file upload UI / no payout UI / no email sending.
 *   • No dispatch enforcement / no contractor approval.
 *   • No raw bank data anywhere (last4 only, deferred to IN-2.d).
 *   • Audit events are append-only.
 *
 * Anchor: RECONCILIATION.md §16 (IN-2.a)
 */

import type { Contractor } from "@/data/types";

// Mirror Contractor.entityType — keep in sync.
export type OnboardingEntityType = NonNullable<Contractor["entityType"]>;
export type OnboardingBusinessRole = NonNullable<Contractor["businessRole"]>;
export type OnboardingAccountabilityMode = NonNullable<
  Contractor["accountabilityMode"]
>;

// ── Invite token ───────────────────────────────────────────────────
export type OnboardingInviteTokenStatus =
  | "pending"
  | "consumed"
  | "revoked"
  | "expired";

export interface OnboardingInviteToken {
  id: string;
  /** Opaque random string, >=32 chars, no PII. */
  token: string;
  contractorId?: string;
  parentId?: string;
  entityType: OnboardingEntityType;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
  status: OnboardingInviteTokenStatus;
  consumedAt?: string;
  consumedBy?: string;
  revokedAt?: string;
  revokedBy?: string;
  revokedReason?: string;
}

// ── Contractor draft ──────────────────────────────────────────────
export interface ContractorOnboardingDraft {
  name?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  entityType?: OnboardingEntityType;
  businessRole?: OnboardingBusinessRole;
  parentEntityId?: string;
  accountabilityMode?: OnboardingAccountabilityMode;
  serviceRegions?: string[];
  serviceZipCodes?: string[];
  tradeType?: string;
  signatures?: OnboardingAgreementSignature[];
  archivedAt?: string;
  archivedBy?: string;
  archiveReason?: string;
  deletedAt?: string;
  deletedBy?: string;
  deleteReason?: string;
  complianceRequirementSettings?: OnboardingComplianceRequirementSettings;
}

export interface OnboardingComplianceRequirementRuleSet {
  licenseRequired?: boolean;
  insuranceRequired?: boolean;
  w9Required?: boolean;
  backgroundCheckRequired?: boolean;
  certificationRequired?: boolean;
  inheritsLicenseFromParent?: boolean;
  inheritsInsuranceFromParent?: boolean;
  inheritsW9FromParent?: boolean;
}

export interface OnboardingComplianceRequirementSettings {
  installer?: OnboardingComplianceRequirementRuleSet;
  crew?: OnboardingComplianceRequirementRuleSet;
  updatedAt?: string;
  updatedBy?: string;
}

// ── Session ───────────────────────────────────────────────────────
export type OnboardingSessionStatus =
  | "draft"
  | "submitted"
  | "approved"
  | "rejected"
  | "reupload_requested";

export type OnboardingAgreementSignature = {
  id: string;
  variant:
    | "1099_msa"
    | "w2_offer_letter"
    | "branch_sublicense"
    | "crew_acknowledgment"
    | "helper_acknowledgment"
    | "certification_agreement";
  version: string;
  signedAt: string;
  signedBy: string;
  typedName?: string;
  ipAddress?: string;
};

export interface OnboardingSession {
  id: string;
  /** Trusted tenant context hydrated from onboarding_sessions.organization_id. */
  organizationId?: string;
  contractorId?: string;
  tokenId: string;
  entityType: OnboardingEntityType;
  parentEntityId?: string;
  currentStep: string;
  completedSteps: string[];
  status: OnboardingSessionStatus;
  draft: ContractorOnboardingDraft;
  /**
   * IN-2.c — agreement signatures captured during onboarding. Mirrors
   * `Contractor.agreementSignatures` shape so IN-2.g can migrate them
   * onto the real contractor row 1:1 without rewriting.
   */
  signatures?: OnboardingAgreementSignature[];
  /**
   * IN-2.d — Captured payout method. Same shape as `Contractor.payoutMethod`
   * so the IN-2.g bridge can lift it 1:1 onto the real contractor row.
   * Tokenized placeholder only — no raw routing / account numbers.
   */
  payoutMethod?: NonNullable<Contractor["payoutMethod"]>;
  startedAt: string;
  lastTouchedAt: string;
  submittedAt?: string;
  reuploadRequestedSteps?: string[];
  reuploadReason?: string;
  /**
   * IN-2.g — Admin handoff outcomes. Terminal markers populated by
   * `approveOnboardingSession()` / `rejectOnboardingSession()`.
   *
   * Approval is recorded HERE on the session — never on
   * `Contractor.onboardingCompletedAt` (that field is owned by P5
   * portal-tour state; see RECONCILIATION §22 P5 collision decision).
   */
  approvedAt?: string;
  approvedBy?: string;
  approvalReason?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  rejectionReason?: string;
}

// ── Audit (append-only) ───────────────────────────────────────────
export type OnboardingAuditAction =
  | "invite_created"
  | "invite_revoked"
  | "invite_consumed"
  | "draft_updated"
  | "step_completed"
  | "session_submitted"
  | "reupload_requested"
  | "document_submitted"
  | "document_verified"
  | "document_rejected"
  | "agreement_signed"
  | "payout_set"
  | "session_approved"
  | "session_rejected";

export interface OnboardingAuditEvent {
  id: string;
  at: string;
  actor: string;
  action: OnboardingAuditAction;
  tokenId?: string;
  sessionId?: string;
  documentId?: string;
  reason?: string;
  metadata?: Record<string, unknown>;
}

export const ONBOARDING_STORAGE_KEY = "koredor.onboarding.v1";
export const DEFAULT_INVITE_EXPIRY_DAYS = 14;
export const MIN_TOKEN_LENGTH = 32;
export const MIN_REASON_LENGTH = 10;

// IN-2.c — document upload limits.
export const MAX_DOCUMENT_UPLOAD_BYTES = 10 * 1024 * 1024;
export const ALLOWED_DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/heic",
  "image/heif",
] as const;
export type AllowedDocumentMimeType =
  (typeof ALLOWED_DOCUMENT_MIME_TYPES)[number];

/** Synthetic contractorId namespace for onboarding sessions. */
export function onboardingContractorId(sessionId: string): string {
  return `onboarding:${sessionId}`;
}
